import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, doc, getDoc, setDoc, serverTimestamp } from '../components/firebase';
import { onSnapshot } from 'firebase/firestore';

const AuthContext = createContext();
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'snaped4338@gmail.com';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    let profileUnsubscribe = null; // holds the Firestore real-time listener

    const authUnsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      // Clean up any previous profile listener when auth state changes
      if (profileUnsubscribe) {
        profileUnsubscribe();
        profileUnsubscribe = null;
      }

      if (firebaseUser) {
        setUser(firebaseUser);
        const isAdminUser = firebaseUser.email === ADMIN_EMAIL;

        try {
          const userDocRef = doc(db, 'Users', firebaseUser.uid);
          const companyDocRef = doc(db, 'Companies', firebaseUser.uid);

          // One-time check for company status to determine role
          const [userDoc, companyDoc] = await Promise.all([
            getDoc(userDocRef),
            getDoc(companyDocRef),
          ]);

          const isKnownCompany = companyDoc.exists();
          let resolvedRole = 'student';

          if (userDoc.exists()) {
            const data = userDoc.data();
            resolvedRole = isKnownCompany ? 'company' : (data.role || (isAdminUser ? 'admin' : 'student'));
          } else if (isKnownCompany) {
            resolvedRole = 'company';
          } else {
            resolvedRole = isAdminUser ? 'admin' : 'student';
            // Create the user document if it's missing
            await setDoc(userDocRef, {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              fullName: firebaseUser.displayName || '',
              photo: firebaseUser.photoURL || '',
              college: '',
              role: resolvedRole,
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
            });
          }

          setUserRole(resolvedRole);

          // Sync role in the document if needed
          if (userDoc.exists() && userDoc.data().role !== resolvedRole) {
            await setDoc(userDocRef, { role: resolvedRole, lastLogin: serverTimestamp() }, { merge: true });
          } else if (userDoc.exists()) {
            await setDoc(userDocRef, { lastLogin: serverTimestamp() }, { merge: true });
          }

          // ── Start a REAL-TIME listener on the Users doc ──────────────────
          profileUnsubscribe = onSnapshot(userDocRef, (snap) => {
            if (snap.exists()) {
              const companyExtra = isKnownCompany ? companyDoc.data() : {};
              setUserProfile({ ...companyExtra, ...snap.data(), role: resolvedRole });
            }
          });

        } catch (err) {
          console.error('AuthContext: Critical Role Resolution Error:', err);
          setUserRole(isAdminUser ? 'admin' : 'student');
        } finally {
          setLoading(false);
        }

      } else {
        // Not logged in
        setUser(null);
        setUserRole(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      authUnsubscribe();
      if (profileUnsubscribe) profileUnsubscribe();
    };
  }, []);

  // Role helpers
  const isAdmin = userRole === 'admin';
  const isCompany = userRole === 'company';
  // 'user' is legacy — treated same as 'student' everywhere in UI
  const isStudent = userRole === 'student' || userRole === 'user';
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user, userRole, isAdmin, isCompany, isStudent,
      isAuthenticated, loading, userProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
